import { EventEmitter2 } from 'eventemitter2';
import debounce from 'lodash/debounce';

// eslint-disable-next-line import/no-cycle
import { createToast } from './foreground/toasts.platform';
import {
  type NetworkDetector,
  type NetworkDetectorState,
  NetworkStatus,
} from './NetworkDetector';
import capitalize from './utils/capitalize';
import makeLogger from './utils/makeLogger';
import { MaybePromise } from './utils/typescriptUtils';

const logger = makeLogger(__filename);

type NetworkDetectorOptions = {
  forcedStatus?: NetworkStatus | null;
  shouldCreateToastOnConfirmedStatusChanges?: boolean;
};

// If the network is very unstable, we don't want frequent toasts alerting us.
const createNetworkStatusToast = debounce(
  (status: NetworkStatus) => {
    if (status === NetworkStatus.Unknown) {
      return;
    }

    createToast({
      category: status === NetworkStatus.Online ? 'success' : 'default',
      content: capitalize(status),
    });
  },
  5000,
  { leading: true },
);

export default abstract class AbstractNetworkDetector<TPlatformState> extends EventEmitter2 implements NetworkDetector {
  status = NetworkStatus.Unknown;
  isConfirmedOffline = false;
  private _forcedStatus: NetworkStatus | null;
  private _hasReceivedKnownNetworkState = false;
  private _hasReceivedNetworkState = false;
  private _shouldCreateToastOnConfirmedStatusChanges: boolean;

  constructor({
    forcedStatus = null,
    shouldCreateToastOnConfirmedStatusChanges = false,
  }: NetworkDetectorOptions = {}) {
    super();

    this._forcedStatus = forcedStatus;
    this._shouldCreateToastOnConfirmedStatusChanges = shouldCreateToastOnConfirmedStatusChanges;
  }

  /**
   * Return the raw platform state needed to decide connectivity.
   *
   * Examples: native NetInfo state, or browser `navigator.onLine`.
   */
  protected abstract _getCurrentPlatformState(): MaybePromise<TPlatformState>;

  /**
   * Convert raw platform state into the platform's unconfirmed connectivity claim.
   *
   * The abstract class owns confirmation and final status updates.
   * Implementations should not perform extra reachability checks here.
   */
  protected abstract _getUnconfirmedStatusFromPlatformState(platformState: TPlatformState): NetworkStatus;

  protected _handleStatusUpdateError(error: unknown) {
    logger.warn(String(error));
  }

  protected _initialize() {
    if (this._forcedStatus) {
      this._setStatus(this._forcedStatus);
      return;
    }

    this._subscribeToPlatformStateChanges();
    this._recheckPlatformState();
  }

  /**
   * Apply platform state already provided by an event listener.
   */
  protected async _onPlatformStateUpdated(platformState: TPlatformState): Promise<void> {
    await this._setStatusFromPlatformState(platformState);
  }

  /**
   * Force a fresh read of the current platform state and apply it. Use when no
   * event listener has handed us state, e.g. on startup or app foreground.
   */
  protected async _recheckPlatformState(): Promise<void> {
    try {
      const platformState = await this._getCurrentPlatformState();

      await this._setStatusFromPlatformState(platformState);
    } catch (error) {
      this._handleStatusUpdateError(error);
    }
  }

  /**
   * Resolve an offline claim using platform-specific reachability details.
   */
  protected abstract _resolveOfflineClaim(platformState: TPlatformState): MaybePromise<NetworkStatus>;

  /**
   * Resolve an online claim using platform-specific reachability details.
   */
  protected abstract _resolveOnlineClaim(platformState: TPlatformState): MaybePromise<NetworkStatus>;

  protected _setStatus(networkStatus: NetworkStatus): void {
    const hadReceivedNetworkStateBeforeUpdate = this._hasReceivedNetworkState;
    const hadReceivedKnownNetworkStateBeforeUpdate = this._hasReceivedKnownNetworkState;
    const hasChanged = !hadReceivedNetworkStateBeforeUpdate || this.status !== networkStatus;

    this._hasReceivedNetworkState = true;
    this.status = networkStatus;
    this.isConfirmedOffline = networkStatus === NetworkStatus.Offline;

    if (networkStatus !== NetworkStatus.Unknown) {
      this._hasReceivedKnownNetworkState = true;
    }

    if (!hasChanged) {
      return;
    }

    this.emit('status-changed', this._getState());

    if (
      hadReceivedKnownNetworkStateBeforeUpdate &&
      networkStatus !== NetworkStatus.Unknown &&
      this._shouldCreateToastOnConfirmedStatusChanges
    ) {
      createNetworkStatusToast(networkStatus);
    }

  }

  /**
   * Implementations should call `_onPlatformStateUpdated(platformState)` when an
   * event already carries the relevant platform state, or `_recheckPlatformState()`
   * when the current platform state should be re-read.
   */
  protected abstract _subscribeToPlatformStateChanges(): void;

  private _getState(): NetworkDetectorState {
    return {
      isConfirmedOffline: this.isConfirmedOffline,
      status: this.status,
    };
  }

  private async _resolveStatusClaim(
    networkStatus: NetworkStatus,
    platformState: TPlatformState,
  ): Promise<NetworkStatus> {
    if (networkStatus === NetworkStatus.Online) {
      return this._resolveOnlineClaim(platformState);
    }

    if (networkStatus === NetworkStatus.Offline) {
      return this._resolveOfflineClaim(platformState);
    }

    return NetworkStatus.Unknown;
  }

  private async _setStatusFromPlatformState(platformState: TPlatformState): Promise<void> {
    try {
      const unconfirmedStatus = this._getUnconfirmedStatusFromPlatformState(platformState);
      const status = await this._resolveStatusClaim(unconfirmedStatus, platformState);

      this._setStatus(status);
    } catch (error) {
      this._handleStatusUpdateError(error);
    }
  }
}
